import { formaterJour, formaterMontant } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { requeteEnvois, requeteMoi } from '../api'
import { EtapesDepot } from '../composants/DepotPieces'
import { Vide } from '../composants/Section'
import { LIBELLES_ETAT_ENVOI, LIBELLES_TYPE_PIECE } from '../libelles'
import { drainer, relireFile, useEtatFile } from '../pwa/etat-file'
import { abandonnerEnvoi, type ElementFile, envoiEnCours, ERREUR_OCCUPE } from '../pwa/file-envoi'
import { EntetePage } from '../composants/Ui'
import { confirmer } from '../composants/confirmation'

// « Mes envois » : ce qui attend encore sur le téléphone (file d'envoi locale), puis chaque
// document reçu par le serveur, photo ou fichier déposé à la main, et son état (reçu, en lecture,
// rangé, à vérifier) avec les mêmes étapes que le suivi du dépôt. Une fois
// traité : entité, émetteur, objet, montant et date limite, avec les liens vers le fichier
// Drive, la pièce et la tâche. Un document hors du périmètre de la personne : « transmis aux
// administrateurs », sans détail. Une pièce qui attend dans l'espace privé de la personne :
// c'est à elle de la vérifier, aucun administrateur ne la voit.

const RANGEMENT: Record<string, string | null> = {
  drive: null,
  local: 'Drive non configuré : les documents rangés vont dans le dossier local du serveur.',
  base: 'Drive non configuré : les documents restent dans l’appli (pièces), sans rangement Drive.',
}

function heure(iso: string | Date) {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris',
  })
}

function raisonAttente(els: ElementFile[]): string {
  const e = els.find((x) => x.derniereErreur)?.derniereErreur
  if (!e) return 'En attente d’envoi'
  if (e === ERREUR_OCCUPE) return 'Serveur occupé, nouvel essai automatique'
  if (e === 'en_cours') return 'Envoi en cours'
  if (e.startsWith('HTTP 5') || !e.startsWith('HTTP'))
    return 'Réseau ou serveur indisponible, nouvel essai automatique'
  return `Refusé pour l’instant (${e}), nouvel essai automatique`
}

function EnAttente() {
  const { elements, bilan, enCours } = useEtatFile()
  const groupes = new Map<string, ElementFile[]>()
  for (const el of elements) groupes.set(el.envoiId, [...(groupes.get(el.envoiId) ?? []), el])
  if (groupes.size === 0 && !bilan?.connexionPerdue) return null
  return (
    <section className="carte section" aria-label="En attente sur ce téléphone">
      <div className="section-titre">
        <h2>En attente d’envoi sur ce téléphone</h2>
      </div>
      {bilan?.connexionPerdue && (
        <p className="alerte">
          Connexion expirée : <a href="/connexion?retour=%2Fenvois">reconnectez-vous</a>. Les photos
          restent sur le téléphone et partiront ensuite.
        </p>
      )}
      {[...groupes.entries()].map(([envoiId, els]) => (
        <div key={envoiId} className="ligne envoi envoi-attente">
          <div className="ligne-contenu">
            <strong>
              {els[0]!.nbFichiers} page{els[0]!.nbFichiers > 1 ? 's' : ''}
            </strong>{' '}
            <span className="badge">En attente d’envoi</span>
            <div className="discret">
              {raisonAttente(els)} · {els.length} fichier{els.length > 1 ? 's' : ''} sur le
              téléphone
            </div>
          </div>
          <div className="ligne-actions">
            <button
              type="button"
              className="bouton-discret"
              disabled={enCours}
              onClick={() => void drainer({ force: true, prioritaire: envoiId })}
            >
              Réessayer maintenant
            </button>
            <button
              type="button"
              className="bouton-discret"
              disabled={envoiEnCours(envoiId)}
              onClick={async () => {
                if (
                  !(await confirmer(
                    'Abandonner cet envoi ? Les photos seront retirées du téléphone.',
                  ))
                )
                  return
                await abandonnerEnvoi(envoiId)
                await relireFile()
              }}
            >
              Abandonner
            </button>
          </div>
        </div>
      ))}
    </section>
  )
}

export function MesEnvois() {
  const client = useQueryClient()
  const { elements } = useEtatFile()
  const liste = useQuery({
    ...requeteEnvois,
    // Tant qu'un document est en route, l'état se rafraîchit seul.
    refetchInterval: (q) =>
      elements.length > 0 ||
      (q.state.data?.envois ?? []).some((e) => e.etat === 'recu' || e.etat === 'en_lecture')
        ? 10_000
        : false,
  })
  const espacesPrives = new Set((useQuery(requeteMoi).data?.espacesPrives ?? []).map((x) => x.id))
  const avertissement = liste.data ? RANGEMENT[liste.data.rangement] : null

  return (
    <main className="mes-envois">
      <EntetePage
        surtitre="Courrier"
        titre="Mes envois"
        action={{ icone: 'photo_camera', libelle: 'Photographier', lien: '/photo' }}
      />
      <p className="discret">
        Tout ce que vous avez envoyé : photos et fichiers. Pour envoyer un fichier :{' '}
        <Link to="/pieces">Pièces</Link>.
      </p>
      {avertissement && <p className="discret">{avertissement}</p>}
      <EnAttente />
      <section className="carte section" aria-label="Documents reçus">
        <div className="section-titre">
          <h2>Reçus par le serveur</h2>
          <button
            type="button"
            className="bouton-discret"
            onClick={() => void client.invalidateQueries({ queryKey: ['envois'] })}
          >
            Actualiser
          </button>
        </div>
        {liste.isPending && <Vide>Chargement…</Vide>}
        {liste.data?.envois.length === 0 && <Vide>Aucun document envoyé depuis ce compte.</Vide>}
        {liste.data?.envois.map((e) => {
          const p = e.piece
          const montant = p?.montants ? (p.montants.aPayer ?? p.montants.ttc ?? null) : null
          const tache = e.taches.find((t) => t.action !== 'verifier_piece') ?? e.taches[0]
          return (
            <article key={e.id} className={`ligne envoi etat-${e.etat}`}>
              {e.aVignette ? (
                <img
                  className="envoi-vignette"
                  src={`/api/envois/${e.id}/vignette`}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <span className="envoi-vignette page-photo-pdf" aria-hidden="true">
                  PDF
                </span>
              )}
              <div className="ligne-contenu">
                <div>
                  <span className={`badge etat-envoi etat-envoi-${e.etat}`}>
                    {LIBELLES_ETAT_ENVOI[e.etat]}
                  </span>{' '}
                  <span className="discret">
                    {heure(e.recuLe)}
                    {e.pages ? ` · ${e.pages} page${e.pages > 1 ? 's' : ''}` : ''}
                    {e.etat === 'recu' && e.fichiersRecus < e.fichiersAttendus
                      ? ` · ${e.fichiersRecus} fichier(s) sur ${e.fichiersAttendus}`
                      : ''}
                    {e.nomFichier ? ` · ${e.nomFichier}` : ''}
                  </span>
                </div>
                {p?.statut && (
                  <EtapesDepot
                    piece={{
                      statut: p.statut,
                      entite: p.entite,
                      transmission: p.transmission,
                      transmiseA: p.transmiseA,
                    }}
                  />
                )}
                {e.etat === 'transmis' && (
                  <p className="discret">
                    Ce document ne relève pas de votre périmètre : il a été transmis aux
                    administrateurs.
                  </p>
                )}
                {p && (
                  <dl className="details envoi-details">
                    {p.entite && (
                      <>
                        <dt>Entité</dt>
                        <dd>{p.entite}</dd>
                      </>
                    )}
                    {p.emetteur && (
                      <>
                        <dt>Émetteur</dt>
                        <dd>{p.emetteur}</dd>
                      </>
                    )}
                    {(p.objet || p.type) && (
                      <>
                        <dt>Objet</dt>
                        <dd>{p.objet ?? (p.type ? LIBELLES_TYPE_PIECE[p.type] : '')}</dd>
                      </>
                    )}
                    {montant !== null && (
                      <>
                        <dt>Montant</dt>
                        <dd className="montant">{formaterMontant(montant, p.devise ?? 'EUR')}</dd>
                      </>
                    )}
                    {p.dateLimite && (
                      <>
                        <dt>Date limite</dt>
                        <dd>{formaterJour(p.dateLimite)}</dd>
                      </>
                    )}
                  </dl>
                )}
                {e.etat === 'a_verifier' && (
                  <p className="alerte">
                    {e.erreur ?? (p?.motifs ?? []).join(' ')}{' '}
                    {p?.entiteId && espacesPrives.has(p.entiteId)
                      ? 'À vérifier par vous, dans votre espace privé (aucun administrateur ne la voit).'
                      : 'Un administrateur ou un responsable va vérifier.'}
                  </p>
                )}
                {e.etat === 'classe' && liste.data?.rangement === 'drive' && (
                  <p className="discret">Rangement dans le Drive en cours.</p>
                )}
                <div className="envoi-liens">
                  {p?.lienDrive && (
                    <a href={p.lienDrive} target="_blank" rel="noopener noreferrer">
                      Fichier dans le Drive
                    </a>
                  )}
                  {p?.id && (
                    <Link to="/pieces/$id" params={{ id: p.id }}>
                      Pièce
                    </Link>
                  )}
                  {tache && (
                    <Link to="/taches/$id" params={{ id: tache.id }}>
                      Tâche{tache.dateDue ? ` (pour le ${formaterJour(tache.dateDue)})` : ''}
                    </Link>
                  )}
                </div>
              </div>
            </article>
          )
        })}
      </section>
    </main>
  )
}
